// TUT-497 — os `validateSearch` das listas que têm visões salvas, fora do
// roteador. As visões (`lib/visoes-de-lista.ts`) aplicam a busca guardada pela
// MESMA validação da rota, e para isso precisam alcançá-la sem importar o
// `router.tsx` (que importa as telas, que importam as visões: ciclo). Os de
// Processos, Perícias e Peças moravam escritos dentro do `createRoute`; o de
// Prazos já morava em `busca-de-prazos.ts` (TUT-476); os das contas eram o
// `buscaOrdenavel` com os filtros de `buscas.ts` (TUT-483). Corpos movidos
// sem mudança, com o comentário de cada regra junto.
import { focoDaUrl, type ChaveDeFoco } from "../lib/foco";
import { motivoDaPericia } from "../lib/indicadores";
import { isColunaDaLista, isDirecaoDeOrdenacao } from "../lib/list-sorting";
import { periodoDaBusca } from "../lib/periodo-de-datas";
import { isProcessSortColumn } from "../lib/process-sorting";
import { isStatusDoProcesso } from "../lib/process-status";
import {
  buscaOrdenavel,
  centavos,
  filtrosDeContasAPagar,
  filtrosDeContasAReceber,
  filtrosDePropostas,
  legalBriefStatusList,
  legalBriefTypeList,
  ligado,
  lista,
  pagina,
  periodoDePropostasDaBusca,
  texto,
  type ContasAPagarSearch,
  type ContasAReceberSearch,
  type PecasSearch,
  type PericiasSearch,
  type ProcessosSearch,
  type PropostasSearch,
} from "./buscas";

// TUT-177: as ~45 linhas de estado de lista na URL que viviam na página
// (leitura crua de `params.get`) viram schema tipado da rota. `page` vira
// número aqui; a página deixa de coagir texto.
export function validarBuscaDeProcessos(busca: Record<string, unknown>): ProcessosSearch {
  const {
    q,
    status,
    claimant,
    subjectId,
    cliente,
    vara,
    reclamada,
    valorMin,
    valorMax,
    from,
    to,
    abertos,
    periodoDe,
    periodoAte,
    sort,
    dir,
    page,
    vista,
    ...resto
  } = busca;
  return {
    ...resto,
    // TUT-538 (D28) — a VISTA da tela: `kanban` é o Kanban de processos, que
    // saiu do menu; a ausência é a Lista. Lista fechada, como `status`.
    vista: vista === "kanban" ? "kanban" : undefined,
    // TUT-476 — o recorte de "Assuntos periciais" (só abertos) e o período
    // de entrada da Série "Processos novos": dia que não existe cai, e
    // período invertido cai inteiro (`periodoDaBusca`).
    abertos: ligado(abertos),
    ...periodoDaBusca({ periodoDe, periodoAte }),
    // TUT-204a: coluna fora da lista fechada é descartada aqui — a URL é
    // entrada do usuário, e o servidor recusaria de qualquer forma; a
    // diferença é a tela não pedir o que já se sabe inválido.
    sort:
      typeof sort === "string" && isProcessSortColumn(sort)
        ? sort
        : undefined,
    dir: dir === "asc" || dir === "desc" ? dir : undefined,
    q: texto(q),
    // TUT-472 (Q034) — lista fechada, como `sort`: um valor fora dela não
    // tem opção no seletor, que mostrava "Todos os status" sobre uma lista
    // filtrada.
    status:
      typeof status === "string" && isStatusDoProcesso(status)
        ? status
        : undefined,
    claimant: texto(claimant),
    subjectId: lista(subjectId),
    cliente: texto(cliente),
    // TUT-248 fase 2 — VARA e RECLAMADA são texto livre, como reclamante; a
    // faixa de valor é centavo inteiro, e lixo vira ausência em vez de
    // viajar para o servidor (que recusaria de qualquer forma).
    vara: texto(vara),
    reclamada: texto(reclamada),
    valorMin: centavos(valorMin),
    valorMax: centavos(valorMax),
    from: texto(from),
    to: texto(to),
    page: pagina(page),
  } as ProcessosSearch;
}

// TUT-181: a fila pericial é a segunda tela com estado de lista tipado —
// o multi-select de status (antes filtro client-side) e a página viajam
// validados; `q` continua com o debounce do useSearchQueryParam.
export function validarBuscaDePericias(busca: Record<string, unknown>): PericiasSearch {
  const { q, status, minhas, motivo, periodoDe, periodoAte, sort, dir, page, ...resto } =
    busca;
  return {
    ...resto,
    q: texto(q),
    status: lista(status),
    minhas: minhas === true || minhas === "true" ? true : undefined,
    // TUT-476 — o motivo (`com`, `sem` ou o id de um motivo) e o período de
    // agendamento que os widgets levam; lixo vira ausência.
    motivo: motivoDaPericia(motivo),
    ...periodoDaBusca({ periodoDe, periodoAte }),
    // TUT-204c: a fila tem filtro próprio, então ela não usa o
    // `buscaOrdenavel` das cinco da família A — mas a REGRA é a mesma:
    // coluna fora da lista fechada é descartada aqui.
    ...(isColunaDaLista("expertises", sort) ? { sort } : {}),
    ...(isDirecaoDeOrdenacao(dir) ? { dir } : {}),
    page: pagina(page),
  } as PericiasSearch;
}

export function validarBuscaDePecas(busca: Record<string, unknown>): PecasSearch {
  const { q, type, status, mine, dueFrom, dueTo, page, sort, dir, ...resto } =
    busca;
  return {
    ...resto,
    q: texto(q),
    type: legalBriefTypeList(type),
    status: legalBriefStatusList(status),
    mine: mine === true || mine === "true" ? true : undefined,
    dueFrom: texto(dueFrom),
    dueTo: texto(dueTo),
    // TUT-204f — ver `/pericias`: filtro próprio, mesma regra da lista
    // fechada.
    ...(isColunaDaLista("legalBriefs", sort) ? { sort } : {}),
    ...(isDirecaoDeOrdenacao(dir) ? { dir } : {}),
    page: pagina(page),
  } as PecasSearch;
}

// TUT-334 — a lista no desenho do mercado: a faixa escolhida no cartão, a
// busca, a ordem e a página viajam no endereço, como nas outras listas.
// TUT-483 — os filtros avançados entram pelo mesmo portão: Situação (a faixa,
// agora repetida), Representante, Processo, Vencimento e Valor.
export function validarBuscaDeContasAPagar(busca: Record<string, unknown>): ContasAPagarSearch {
  return buscaOrdenavel("accountsPayable", busca, filtrosDeContasAPagar);
}

// TUT-483 — a lista no padrão das outras: busca, ordem, página e os filtros
// avançados (Situação, Cliente, Marco, Rota, Processo e Valor) no endereço.
export function validarBuscaDeContasAReceber(busca: Record<string, unknown>): ContasAReceberSearch {
  return buscaOrdenavel("accountsReceivable", busca, filtrosDeContasAReceber);
}

// TUT-584 — Propostas no padrão das listas: busca, ordem, página e os filtros
// (status, modalidade, cliente e o período criada/enviada) no endereço. A pilha
// de pop-ups dos quatro contadores (TUT-343) entra por cima, e `foco`/`processo`
// saem do `busca` ANTES, para não vazar crus pelo `...resto` do helper. O link
// antigo (`?periodo=enviada&de=…&ate=…`, TUT-488) segue valendo.
export function validarBuscaDePropostas(busca: Record<string, unknown>): PropostasSearch {
  const { foco, processo, periodo: _periodo, de: _de, ate: _ate, ...resto } = busca;
  return {
    ...buscaOrdenavel<"proposals", PropostasSearch>("proposals", resto, filtrosDePropostas),
    ...(focoDaUrl(foco) ? { foco: foco as ChaveDeFoco, processo: texto(processo) } : {}),
    ...periodoDePropostasDaBusca(busca),
  };
}
